import { useCallback, useEffect, useState } from 'react';

import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Modal as ModalAntD,
  Radio,
  Spin,
  Tag as TagAntD,
  Typography,
} from 'antd';
import moment from 'moment';
import { T } from 'react-translator-component';

const Tag: any = TagAntD;

import {
  Container,
  CustomerTenantContainer,
  lastLine,
  MessageBox,
  MoreDetailsContainer,
  RechargeContainer,
  StrongValue,
  Subtitle,
  Title,
  Value,
  ValueContainer,
  ValueText,
} from './styled';

import {
  getExternalChargeId,
  getExternalReferenceId,
  getUserUuid,
  getWalletManualAdjustmentOperator,
  nfeResend,
  sendRefund,
} from '../../../hooks/financial';
import { useTheme } from '../../../hooks/useTheme';
import {
  MANUAL_RECONCILE_ALLOWED_STATUSES,
  RECONCILIATION_STATUS_META,
} from '../../../hooks/reconciliation';
import { hasReconciliationRole } from '../../../utils/hasReconciliationRole/hasReconciliationRole';
import { PaymentTypes } from '../../../types/responses/Transactions';
import { TenantLogoIcon } from '../../atoms/icons/TenantLogoIcon';
import ReconciliationManualModal, {
  ManualMode,
  ManualTargetRow,
} from '../../organisms/ReconciliationManualModal';
import {
  DataType,
  FinancialInfoType,
  formatManualAdjustmentValue,
  isManualAdjustmentRow,
} from '../../organisms/FinancialTable/useFinancialTable';

interface FinancialDrawerDetailsProps {
  data: DataType | null;
  handleStatus: any;
  handleUpdatePage: (shouldResetPage?: boolean) => void;
  setOpenDrawerProps: any;
}

const Modal: any = ModalAntD;

const userUuidCache = new Map<number, string>();

const resolveUserUuid = async (userPk: number): Promise<string> => {
  const cached = userUuidCache.get(userPk);
  if (cached) return cached;
  const uuid = await getUserUuid(userPk);
  userUuidCache.set(userPk, uuid);
  return uuid;
};

const adjustmentAuditCache = new Map<number, any>();

const resolveAdjustmentAudit = async (pk: number) => {
  if (adjustmentAuditCache.has(pk)) return adjustmentAuditCache.get(pk);
  const entry = (await getWalletManualAdjustmentOperator(pk)) ?? null;
  adjustmentAuditCache.set(pk, entry);
  return entry;
};

export type RefundData = {
  value?: number;
};

const rawMaskMoney = (value: string) => {
  const onlyDigits = value
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);

  return digitsFloat;
};

const mascaraMoeda = (
  value: string = '0',
  locale = 'pt-BR',
  currency = 'BRL',
) => {
  const digitsFloat = rawMaskMoney(value);

  return maskCurrency(digitsFloat, locale, currency);
};

const maskCurrency = (valor, locale = 'pt-BR', currency = 'BRL') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(valor);
};

const handleNFStatus = (nfeStatus: string) => {
  if (!nfeStatus) {
    return;
  }

  const allStatus = {
    permissao_negada: 'Permissão Negada',
    erro_autorizacao: 'Erro Autorização',
    processando_autorizacao: 'Processando Autorização',
    autorizado: 'Emitida',
  };

  return (
    <div style={{ marginRight: 10 }}>{allStatus[nfeStatus] || nfeStatus}</div>
  );
};

const resendInvoiceDisabled = (nfeStatus: string) => {
  if (!nfeStatus) {
    return true;
  }

  return (
    nfeStatus !== 'erro_autorizacao' &&
    nfeStatus !== 'rejeitado' &&
    nfeStatus !== 'error_cs'
  );
};

const FinancialDrawerDetails = ({
  data,
  handleStatus,
  handleUpdatePage,
  setOpenDrawerProps,
}: FinancialDrawerDetailsProps) => {
  const { theme } = useTheme();

  const isLeroyMerlin = theme === 'leroy_merlin';

  const user = JSON.parse(localStorage.getItem('auth-user'));

  const shouldShowBeneficiaryAccounts = user?.permissions.some(
    (permission) =>
      permission.role_id === 'DASH_SHOW_BENEFICIARY_ACCOUNTS' &&
      !!permission.status,
  );

  const isDeposit = data && data.type === FinancialInfoType.USER;
  const isTransaction = data && data.type === ('TRANSACTION' as any);

  const [isRefundModalOpen, setRefundModalOpen] = useState(false);
  const [manualMode, setManualMode] = useState<ManualMode>('reconcile');
  const [manualTarget, setManualTarget] = useState<ManualTargetRow | null>(null);

  const openManualModal = (mode: ManualMode) => {
    if (!data) return;
    const parsed = data.paidTimestamp
      ? moment(data.paidTimestamp, 'DD/MM/YYYY - HH:mm', true)
      : moment();
    const period = (parsed.isValid() ? parsed : moment()).format('YYYY-MM');
    setManualMode(mode);
    setManualTarget({
      transactionPk: data.key,
      referencePeriod: period,
      autoStatus: data.autoStatus ?? null,
      effectiveStatus: data.effectiveStatus ?? null,
      manualReconciledAt: data.manualReconciledAt ?? null,
      manualReason: data.manualReason ?? null,
      manualReconciledByUserPk: data.manualReconciledByUserPk ?? null,
    });
  };

  const [isSendRefund, setSendRefund] = useState(false);

  const [isPartialAmount, setPartialAmount] = useState(false);
  const [partialAmountValue, setPartialAmountValue] = useState<string>();
  const [hasValidationError, setValidationError] = useState(false);
  const [externalId, setExternalId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adjustmentAudit, setAdjustmentAudit] = useState<any>(null);
  const [adjustmentAuditLoading, setAdjustmentAuditLoading] = useState(false);
  const [adjustmentAuditError, setAdjustmentAuditError] = useState(false);

  const isManualAdjustment = isManualAdjustmentRow(data);

  const showPartialLabel = Boolean(data?.paymentTransactionAmountRefunded);

  const onRefundRequest = async () => {
    setSendRefund(true);

    if (data) {
      try {
        await sendRefund(
          data.key,
          partialAmountValue
            ? Number(rawMaskMoney(partialAmountValue))
            : undefined,
        );

        Modal.success({
          title: 'Sua solicitação de estorno foi enviada com sucesso!',
        });

        setTimeout(() => handleUpdatePage(), 2000);

        const payload: any = {};

        if (partialAmountValue) {
          payload.paymentTransactionAmountRefunded =
            data?.paymentTransactionAmountRefunded +
            Number(rawMaskMoney(partialAmountValue));
          payload.value =
            data?.value - Number(rawMaskMoney(partialAmountValue));
          payload.paymentTransactionAmountHumanReadable = mascaraMoeda(
            (data?.value - Number(rawMaskMoney(partialAmountValue))).toFixed(2),
          );
        } else {
          payload.paymentTransactionAmountRefunded = Number(
            rawMaskMoney(data?.total),
          );
          payload.value = 0;
          payload.paymentTransactionAmountHumanReadable = mascaraMoeda(
            (0).toFixed(2),
          );
          payload.paymentStatus = 'refunded';
        }

        setOpenDrawerProps({
          visible: true,
          data: {
            ...data,
            ...payload,
          },
        });
      } catch {
        Modal.error({
          title: 'Ocorreu um erro ao tentar fazer o estorno!',
        });
      } finally {
        setRefundModalOpen(false);
        setSendRefund(false);

        setPartialAmount(false);
        setPartialAmountValue(undefined);
      }
    }
  };

  const tryToGetExternalChargeId = useCallback(async () => {
    try {
      setLoading(true);

      if (!data) {
        return null;
      }

      let externalRef;
      if (PaymentTypes.WALLET === data.paymentTypeString) {
        const userUuid = await resolveUserUuid(data.userId);
        externalRef = await getExternalReferenceId(data.key, userUuid);
      } else if (
        [PaymentTypes.PAGBANK_CARD, PaymentTypes.PAGBANK_PIX].includes(
          data.paymentTypeString,
        )
      ) {
        externalRef = await getExternalChargeId(data.externalPaymentReference);
      } else {
        externalRef = data.externalPaymentReference;
      }

      setExternalId(externalRef);
      setError(null);
    } catch {
      setError(
        'Não foi possível obter o ID externo do pagamento. Por favor, tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    tryToGetExternalChargeId();
  }, [tryToGetExternalChargeId]);

  useEffect(() => {
    let active = true;

    if (!isManualAdjustment || !data?.key) {
      setAdjustmentAudit(null);
      setAdjustmentAuditError(false);
      return;
    }

    setAdjustmentAuditLoading(true);
    setAdjustmentAuditError(false);
    resolveAdjustmentAudit(data.key)
      .then((entry) => {
        if (active) setAdjustmentAudit(entry);
      })
      .catch(() => {
        if (active) setAdjustmentAuditError(true);
      })
      .finally(() => {
        if (active) setAdjustmentAuditLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isManualAdjustment, data?.key]);

  return (
    <Container>
      <CustomerTenantContainer>
        <a className='customer-name'>{data?.customer}</a>

        <TenantLogoIcon
          size={30}
          logoIconUrl={data?.tenant}
          tenantAlias={data?.alias}
        />
      </CustomerTenantContainer>

      <ValueContainer>
        <ValueText isDeposit={isDeposit}>
          {isManualAdjustment
            ? formatManualAdjustmentValue(data?.value)
            : isDeposit
            ? `+ ${data?.paymentTransactionAmountHumanReadable}`
            : data?.paymentTransactionAmountHumanReadable}
        </ValueText>
        {isTransaction && (
          <RechargeContainer>
            <a className='charge-box'>{data?.chargeBox}</a>
            <u className='recharge-id'>{data?.key}</u>
            <a className='external-payment-reference'>
              Código da transação:{''}
              {data.paymentTypeString !== PaymentTypes.WALLET &&
              data.externalPaymentReference === null ? (
                ' Código não encontrado'
              ) : loading ? (
                <Spin
                  size='small'
                  style={{ marginLeft: 12, marginTop: 2 }}
                  className='gray-spinner'
                />
              ) : error ? (
                <span className='error-message'>
                  {' '}
                  Erro ao carregar o código
                </span>
              ) : Array.isArray(externalId) ? (
                externalId.map((transaction: string, index: number) => (
                  <div className='external-id-array' key={index}>
                    {transaction}
                  </div>
                ))
              ) : (
                ' ' + externalId
              )}
            </a>
            {data?.externalPaymentReference && (
              <a className='external-payment-reference'>
                Referência da transação: {''} {data.externalPaymentReference}
              </a>
            )}
          </RechargeContainer>
        )}
      </ValueContainer>

      <MoreDetailsContainer>
        <Title isFirstItem>{T('Details')}</Title>

        <Subtitle style={{ marginTop: 8 }}>
          Forma de pagamento: <Value>{data?.paymentType}</Value>
        </Subtitle>

        <Subtitle>
          Data e hora: <Value>{data?.paidTimestamp}</Value>
        </Subtitle>

        <Subtitle
          style={showPartialLabel || data?.paymentStatus ? undefined : lastLine}
        >
          Status
          <Value style={{ position: 'relative', bottom: 3 }}>
            {handleStatus(
              data?.paymentStatus,
              showPartialLabel,
              isManualAdjustment,
              data?.statusBtoB,
            )}
          </Value>
        </Subtitle>

        {/* MV2-1909: seção de Conciliação (só pra Recarga que tem reconciliation row).
            MV2-1904: role DASH_PAYMENT_RECONCILIATION gatekeeps a seção inteira. */}
        {isTransaction && data?.effectiveStatus && hasReconciliationRole() && (() => {
          const effective = data.effectiveStatus;
          const meta = RECONCILIATION_STATUS_META[effective];
          const isManualReconciliation = effective === 'RECONCILED_MANUAL';
          // Pendente = status permitido pra ação manual. Usa autoStatus quando chegou
          // (fonte canônica); se vier vazio, cai pro effectiveStatus (pode acontecer
          // quando a tabela do financial foi populada antes do ETL incluir auto_status).
          const checkStatus = data.autoStatus || effective;
          const isPending =
            MANUAL_RECONCILE_ALLOWED_STATUSES.has(checkStatus) &&
            !isManualReconciliation;

          return (
            <>
              <Subtitle style={{ marginTop: 12 }}>
                Conciliação
                <Value style={{ position: 'relative', bottom: 3, marginLeft: 8 }}>
                  {meta ? (
                    <Tag color={meta.color}>{meta.label}</Tag>
                  ) : (
                    <Tag>{effective}</Tag>
                  )}
                </Value>
              </Subtitle>

              {isManualReconciliation && (
                <>
                  {data.manualReason && (
                    <Subtitle>
                      Motivo da conciliação manual:{' '}
                      <Value>{data.manualReason}</Value>
                    </Subtitle>
                  )}
                  {data.manualReconciledAt && (
                    <Subtitle>
                      Data da conciliação manual:{' '}
                      <Value>
                        {moment(data.manualReconciledAt).format(
                          'DD/MM/YYYY HH:mm',
                        )}
                      </Value>
                    </Subtitle>
                  )}
                  {data.manualReconciledByUserPk != null && (
                    <Subtitle>
                      Responsável:{' '}
                      <Value>user #{data.manualReconciledByUserPk}</Value>
                    </Subtitle>
                  )}
                </>
              )}

              {(isPending || isManualReconciliation) && (
                <Subtitle style={{ marginTop: 8 }}>
                  <Button
                    type={isPending ? 'primary' : 'default'}
                    danger={isManualReconciliation}
                    onClick={() =>
                      openManualModal(isPending ? 'reconcile' : 'undo')
                    }
                  >
                    {isPending
                      ? 'Conciliar manualmente'
                      : 'Desfazer conciliação manual'}
                  </Button>
                </Subtitle>
              )}
            </>
          );
        })()}

        {isManualAdjustment && (
          <>
            <Subtitle>
              {T('Wallet changed for')}: <Value>{data?.customer}</Value>
            </Subtitle>

            <Subtitle style={lastLine}>
              {T('Changed by')}:{' '}
              <Value>
                {adjustmentAuditLoading ? (
                  <Spin
                    size='small'
                    style={{ marginLeft: 12, marginTop: 2 }}
                    className='gray-spinner'
                  />
                ) : adjustmentAuditError ? (
                  <span className='error-message'> {T('Failed to load')}</span>
                ) : (
                  adjustmentAudit?.actorName ??
                  adjustmentAudit?.actorEmail ??
                  '—'
                )}
              </Value>
            </Subtitle>
          </>
        )}

        {isTransaction && (
          <Subtitle style={showPartialLabel ? undefined : lastLine}>
            NF.:
            <Value>
              {handleNFStatus(data?.nfeStatus)}

              <Button disabled={!data?.nfeUrls?.length} size='small'>
                <DownloadOutlined
                  onClick={() => {
                    if (data?.nfeUrls?.length > 0) {
                      const [nfeUrl] = data?.nfeUrls;
                      window.open(nfeUrl, '_blank').focus();
                    }
                  }}
                />
              </Button>
            </Value>
          </Subtitle>
        )}

        {showPartialLabel && (
          <>
            <Subtitle>
              Valor total
              <Value>{data?.total}</Value>
            </Subtitle>

            <Subtitle style={lastLine}>
              Valor devolvido
              <Value>
                {mascaraMoeda(
                  data?.paymentTransactionAmountRefunded.toFixed(2),
                )}
              </Value>
            </Subtitle>

            <Subtitle
              style={{
                ...lastLine,
                color: 'black',
                fontSize: 13,
                backgroundColor: '#F3F4F6',
                paddingTop: 12,
                marginLeft: -16,
                marginRight: -16,
                paddingLeft: 16,
                paddingRight: 16,
                marginBottom: -12,
                paddingBottom: 12,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              }}
            >
              Valor disponível
              <StrongValue>{mascaraMoeda(data?.value.toFixed(2))}</StrongValue>
            </Subtitle>
          </>
        )}
      </MoreDetailsContainer>

      {isTransaction && (
        <MoreDetailsContainer>
          <Title>{T('Recharge composition')}</Title>

          {data?.moneyPerTransactionIncomeHumanReadable && (
            <Subtitle style={{ marginTop: 8 }}>
              Start
              <Value>{data?.moneyPerTransactionIncomeHumanReadable}</Value>
            </Subtitle>
          )}

          {data?.energyCostHumanReadable && (
            <Subtitle>
              {`Energia (${
                data?.energyHumanReadable ?? '-'
              }) x Preço do kWh (R$ ${
                data?.moneyPerKilowattIncome?.toFixed(2) ?? '0.00'
              })`}
              <Value>{data?.energyCostHumanReadable}</Value>
            </Subtitle>
          )}

          {data?.taxIncomeHumanReadable && (
            <Subtitle>
              Taxa operacional
              <Value>{data?.taxIncomeHumanReadable}</Value>
            </Subtitle>
          )}

          {data?.moneyVoucherHumanReadable && (
            <Subtitle>
              Voucher Desconto
              <Value>{data?.moneyVoucherHumanReadable}</Value>
            </Subtitle>
          )}

          {data?.paymentTransactionAmountHumanReadable && (
            <Subtitle style={{ border: 'none', marginBottom: 0 }}>
              Total pago
              <StrongValue>
                {data?.paymentTransactionAmountHumanReadable}
              </StrongValue>
            </Subtitle>
          )}
        </MoreDetailsContainer>
      )}

      {isTransaction &&
        data?.splits?.length > 0 &&
        (isLeroyMerlin || shouldShowBeneficiaryAccounts) && (
          <MoreDetailsContainer>
            <Title>{T('Beneficiary Accounts')}</Title>

            {data.splits.map((split, index) => (
              <Subtitle key={index}>
                {split.receiver_account_name} ({split.value}%)
                <Value>
                  {maskCurrency(
                    split.amount,
                    data?.moneyCoin === 'COL' ? 'es-CO' : 'pt-BR',
                    data?.moneyCoin || 'BRL',
                  )}
                </Value>
              </Subtitle>
            ))}
          </MoreDetailsContainer>
        )}

      <div style={{ marginTop: 24 }}>
        <Button
          disabled={
            data?.paymentStatus === 'refunded' ||
            data?.paymentStatus !== 'approved' ||
            isManualAdjustment
          }
          loading={showPartialLabel && isSendRefund}
          size='large'
          style={{ marginBottom: 16 }}
          block
          onClick={async () => {
            if (showPartialLabel) {
              await onRefundRequest();
            } else {
              setRefundModalOpen(true);
            }
          }}
        >
          {showPartialLabel ? 'Fazer estorno total' : 'Fazer estorno'}
        </Button>

        <Button
          size='large'
          disabled={resendInvoiceDisabled(data?.nfeStatus)}
          style={{ marginBottom: 16 }}
          block
          onClick={() => {
            Modal.confirm({
              title: 'Deseja reenviar a nota fiscal?',
              icon: <ExclamationCircleOutlined />,
              okText: 'Reenviar',
              cancelText: 'Cancelar',
              okButtonProps: {
                danger: true,
              },
              onOk: async () => {
                if (data) {
                  try {
                    await nfeResend(data.key);

                    setOpenDrawerProps({
                      visible: true,
                      data: {
                        ...data,
                        nfeStatus: 'autorizado',
                      },
                    });

                    Modal.success({
                      title:
                        'Sua solicitação de reenvio da nota fiscal foi enviada com sucesso!',
                    });
                  } catch {
                    Modal.error({
                      title:
                        'Ocorreu um erro ao tentar reenviar a nota fiscal!',
                    });
                  }
                }
              },
            });
          }}
        >
          Reenviar NF
        </Button>
      </div>

      <Modal
        destroyOnClose
        closable={!isSendRefund}
        maskClosable={!isSendRefund}
        visible={isRefundModalOpen}
        onCancel={() => {
          setRefundModalOpen(false);

          setPartialAmount(false);
          setPartialAmountValue(undefined);
          setValidationError(false);
        }}
        okButtonProps={{
          size: 'large',
          loading: isSendRefund,
          disabled: hasValidationError,
        }}
        onOk={onRefundRequest}
        okText='Continuar'
        cancelButtonProps={{ size: 'large', disabled: isSendRefund }}
      >
        <Typography.Title level={3}>Fazer estorno</Typography.Title>

        <div style={{ marginTop: 16 }}>
          {data?.paymentTransactionAmountRefunded
            ? 'Valor total restante'
            : 'Valor total'}

          {data?.value && (
            <Input
              readOnly
              style={{ marginTop: 4 }}
              size='large'
              value={mascaraMoeda((data?.value).toFixed(2))}
            />
          )}
        </div>

        <div style={{ marginTop: 16, marginBottom: 4 }}>Tipo estorno</div>

        <Radio.Group onChange={() => {}} defaultValue={0}>
          <Radio
            value={0}
            style={{ fontWeight: 300 }}
            onClick={() => setPartialAmount(false)}
          >
            Total
          </Radio>

          <Radio
            value={1}
            style={{ fontWeight: 300 }}
            onClick={() => setPartialAmount(true)}
          >
            Parcial
          </Radio>
        </Radio.Group>

        {isPartialAmount && (
          <div style={{ marginTop: 16 }}>
            Valor
            <Input
              style={{ marginTop: 4 }}
              size='large'
              onChange={(event) => {
                setPartialAmountValue(
                  mascaraMoeda(
                    event.target.value,
                    data?.moneyCoin === 'COL' ? 'es-CO' : 'pt-BR',
                    data?.moneyCoin || 'BRL',
                  ),
                );

                setValidationError(
                  Number(rawMaskMoney(event.target.value)) > (data?.value || 0),
                );
              }}
              value={partialAmountValue}
            />
            {hasValidationError && (
              <MessageBox>
                O valor parcial não pode ser maior que o valor total{' '}
                {data?.paymentTransactionAmountRefunded ? 'restante' : ''}
              </MessageBox>
            )}
          </div>
        )}
      </Modal>

      <ReconciliationManualModal
        visible={manualTarget !== null}
        mode={manualMode}
        target={manualTarget}
        onClose={() => setManualTarget(null)}
        onDone={() => {
          handleUpdatePage(false);
          setOpenDrawerProps((prev) => ({ ...prev, visible: false }));
        }}
      />
    </Container>
  );
};

export default FinancialDrawerDetails;
